import PubSub from "pubsub-js";
import {parse} from "query-string";
import React, {Fragment} from "react";
import {CornerDetection} from "../../constants/CornerDetection";
import {DecodeResult, DecoderPair} from "../../constants/DecoderPair";
import {DeviceDetection} from "../../constants/DeviceDetection";
import {FlowStates} from "../../constants/FlowStates";
import {Logger} from "../../constants/Logger";
import {CaptureImageData, PauseVideoActivitiesData, PubSubEvents, SignalEvents} from "../../constants/PubSubEvents";
import {Routes} from "../../constants/Routing";
import "./Video.css";

export interface VideoProps {
  uploadId: string;
  className?: string;
  debug: boolean;
  enableLocalDecode: boolean;
  onImageCaptured: (capturedImage: CapturedImage | undefined) => void;
  onVideoReady: () => void;
  resolution: Resolution;
  canvasRef?: string;
}

export interface VideoState {
  attempts: number;
  videoElement?: HTMLVideoElement;
  videoStream?: MediaStream;
  videoTrack?: MediaStreamTrack;
  overlayCanvas?: HTMLCanvasElement;
  overlayContext?: CanvasRenderingContext2D;
  imageDataContext?: CanvasRenderingContext2D;
  failedInFrame?: number;
  cornerDetector?: CornerDetector;
}

export interface OverlayCanvas {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
}

export interface CornerDetector {
  currentlyInFrame?: boolean;
  consecutiveInFrame?: number;
  inverseThreshold: boolean;
  threshold?: number;
  tracingOnly?: boolean;
}

export interface Resolution {
  width: number;
  height: number;
}

export const SevenTwenty: Resolution = {
  height: 720,
  width: 1280,
};

export const FourK: Resolution = {
  height: 2160,
  width: 4096,
};

export interface CapturedImage {
  image?: string;
  localDecodeAttempts?: number;
  resolution?: Resolution;
  result: CapturedImageResult;
  decodeOffset?: number;
}

export enum CapturedImageResult {
  CAPTURED,
  FAILED_OTHER,
  FAILED_DECODE_TIMEOUT,
}

export class Video extends React.Component<VideoProps, VideoState> {
  private componentIsMounted = true;
  private decoderPairs: DecoderPair[];
  private pauseVideoActivities = false;
  private inFrameOverride = false;
  private timeoutStarted = false;

  private cornerDetector: CornerDetector = {
    consecutiveInFrame: 0,
    currentlyInFrame: false,
    inverseThreshold: false,
    threshold: 120,
    tracingOnly: false,
  };

  constructor(props: VideoProps) {
    super(props);

    this.decoderPairs = Array<DecoderPair>();
    if ((props.enableLocalDecode || props.debug) && DeviceDetection.webWorkersSupported()) {
      const logicalCores = window.navigator.hardwareConcurrency;
      let logicalCoresPairs: number;
      if (logicalCores != null) {
        // odds are decoding will be slower than sending to back if less than 2 threads
        if (logicalCores < 2) {
          logicalCoresPairs = 0;
        } else {
          logicalCoresPairs = Math.floor(window.navigator.hardwareConcurrency / 2);
        }
      } else {
        // Assume 4 logical cores since apple is the only phone that doesn't support the concurrency function
        // and estimating the cores would take significant time.
        logicalCoresPairs = 2;
      }

      Logger.infoWithId(this.props.uploadId, `Determined logical core pairs: ${logicalCoresPairs}`);
      for (let i = 0; i < logicalCoresPairs; i++) {
        let offsetTrack = i;
        if (logicalCoresPairs === 2) { // Aka iphone
          offsetTrack = i + 2;
        }
        this.decoderPairs.push(new DecoderPair(this.onDecodeResult, offsetTrack, this.props.uploadId));
      }
    }

    PubSub.subscribe(SignalEvents.CAPTURE_IMAGE, this.captureListener);
    PubSubEvents.subscribeSetPauseVideoActivities(this.pauseVideoActivitiesListener);
    PubSubEvents.subscribeResetForNewImage(this.resetForNewImageListener);

    let tracingOnly = false;
    if (this.props.debug === true && "true" === parse(Routes.BROWSER_HISTORY.location.search).tracingOnly) {
      tracingOnly = true;
    }
    this.cornerDetector.tracingOnly = tracingOnly;
    this.state = {
      attempts: 0,
      failedInFrame: 0,
    };
  }

  public render() {
    let debug = (null);
    if (this.props.debug && this.state.attempts != null
      && this.state.failedInFrame != null) {
      debug = (
       <Fragment>
          <h1 className={"Video-debug-decode"}>{"Local Decode Attempts: " + this.state.attempts}</h1>
          <h1 className={"Video-debug-inFrame"}>{"Out of Frame: " + this.state.failedInFrame}</h1>
       </Fragment>
      );
    }
    return (
      <Fragment>
        <video
          className={this.props.className}
          ref={this.setupCameraFeed}
          autoPlay={true}
          playsInline={true}
          onLoadedMetadata={this.onVideoLoadedMetaData}
        />
        <canvas
          className={"Camera-overlay-canvas"}
          ref={this.setupOverlayCanvas}
        />
        {debug}
     </Fragment>
    );
  }

  public componentWillUnmount() {
    this.componentIsMounted = false;
    const stream = this.state.videoStream;
    if (!!stream) {
      stream.getVideoTracks().forEach( (v) => v.stop());
    }
    PubSub.unsubscribe(this.captureListener);
    PubSub.unsubscribe(this.pauseVideoActivitiesListener);
    PubSub.unsubscribe(this.resetForNewImageListener);
    this.decoderPairs.forEach((decoderPair) => {
      decoderPair.terminate();
    });
  }

  // only update on ui props/state effects
  public shouldComponentUpdate(nextProps: VideoProps, nextState: VideoState) {
    const props = this.props;
    const state = this.state;
    if (props !== nextProps) {
      // for some reason it thinks props are changing :(
      if (props.resolution !== nextProps.resolution ||
          props.onVideoReady !== nextProps.onVideoReady ||
          props.className !== nextProps.className ||
          props.debug !== nextProps.debug) {
        return true;
      }
    } else if (state !== nextState) {
      if (state.videoElement !== nextState.videoElement ||
          state.videoStream !== nextState.videoStream ||
          state.videoTrack !== nextState.videoTrack ||
          (state.failedInFrame !== nextState.failedInFrame && nextState.failedInFrame != null) ||
          (nextProps.debug && state.attempts !== nextState.attempts)) {
        return true;
      }
    }
    return false;
  }

  public componentDidCatch(error: any, info: any) {
    Logger.error(`Video component caught an error globally.\nError: ${error}\nInfo:${info}`, error, info);
    PubSubEvents.setFlowState(FlowStates.CAMERA_PROBLEM);
  }

  private setupCameraFeed = (video: HTMLVideoElement): void => {
     if (!!video) {

       const nav: any = navigator;
       const userMedia = nav.getUserMedia || nav.webkitGetUserMedia || nav.mozGetUserMedia;
       const constraints: MediaStreamConstraints = {
           audio: false,
           video: {
               facingMode: "environment",
               // lower resolution after image capture if needed.
               height: { ideal: SevenTwenty.height },
               width: { ideal: SevenTwenty.width },
           },
       };
       const videoSuccess = (stream: MediaStream) => {
           video.srcObject = stream;
           this.setState({
               videoElement: video,
               videoStream: stream,
           });
       };
       const videoError = (error: any) => {
           if (error.name === "NotAllowedError") {
               Logger.warn(`User did not allow access to camera: ${error}`);
               PubSubEvents.setFlowState(FlowStates.MUST_GRANT_PERMISSION);
               return;
           }
           Logger.warn(`Encountered Exception when calling getUserMediaFn: ${error}`);
           PubSubEvents.setFlowState(FlowStates.CAMERA_PROBLEM);
       };

       if (!userMedia && nav.mediaDevices) {
            nav.mediaDevices.getUserMedia(constraints).then(videoSuccess, videoError);
       } else if (userMedia) {
           userMedia.call(nav, constraints, videoSuccess, videoError);
       } else {
           PubSubEvents.setFlowState(FlowStates.CAMERA_PROBLEM);
       }
    }
  };

  private setupOverlayCanvas = (canvas: HTMLCanvasElement): void => {
    if (!!canvas) {
      this.setState({
          failedInFrame: 0,
          overlayCanvas: canvas,
      });
      this.cornerDetector.currentlyInFrame = false;
      const ctx = canvas.getContext("2d");
      if (!!ctx) {
          this.setState({
              overlayContext: ctx,
          });
      }
    }
  };

  private onImageCaptured = (capturedImage: CapturedImage | undefined) => {
    this.pauseVideoActivities = true;
    this.props.onImageCaptured(capturedImage);
  };

  private onVideoLoadedMetaData = () => {
    // https://oberhofer.co/mediastreamtrack-and-its-capabilities/
    // explains the timeout
    setTimeout(this.onCapabilitiesReady, 500);
  };

  private getFirstTrack = (): MediaStreamTrack | undefined => {
    const stream = this.state.videoStream;
    if (!stream) {
      return undefined;
    }

    const tracks = stream.getVideoTracks();
    if (tracks.length === 0) {
      return undefined;
    }

    return tracks[0];
  };

  private onCapabilitiesReady = () => {
    this.props.onVideoReady();
  };

  private onDecodeResult = (result: DecodeResult) => {
    Logger.infoWithId(this.props.uploadId, `onDecodeResult with offset:${result.decodeOffset}`);
    if (!this.componentIsMounted) {
      return;
    }
    const capturedImage: CapturedImage = {
      decodeOffset: result.decodeOffset,
      image: result.dataUrl,
      localDecodeAttempts: this.state.attempts,
      resolution: {
        height: result.height,
        width: result.width,
      },
      result: CapturedImageResult.CAPTURED,
    };
    this.decoderPairs.forEach((pair) => {
      pair.setPaused(true);
    });
    this.onImageCaptured(capturedImage);
  };

  private pauseVideoActivitiesListener = (msg: any, data: PauseVideoActivitiesData) => {
    this.pauseVideoActivities = data.paused;
  };

  private resetForNewImageListener = () => {
    this.timeoutStarted = false;
    this.inFrameOverride = false;
    this.setState({
      failedInFrame: 0,
    });
  };

  private captureListener = (msg: any, data: CaptureImageData) => {
    this.pauseVideoActivities = false;
    if (!this.inFrameOverride) {
      this.inFrame();
    }
    if (!this.timeoutStarted) {
      this.timeoutStarted = true;
      setTimeout(this.timeoutInFrameCheck, this.cornerDetector.tracingOnly ? 1000000 : 10000);
    }
    if (data.decodeNow && this.decoderPairs.length > 0) {
      // if we had success on front, but not on back need to unpause these (shouldn't happen)
      this.decoderPairs.forEach((pair) => {
        pair.setPaused(false);
      });
      // current stats show a avg backend time of 30 seconds for valid uploads
      setTimeout(this.timeoutDecodeCapture, 30000);
      this.getImageAndDecode();
      return;
    }
    if (!this.cornerDetector.tracingOnly) {
      this.captureImage();
    }
  };

  private timeoutInFrameCheck = () => {
    this.inFrameOverride = true;
  };

  private timeoutDecodeCapture = () => {
    if (this.componentIsMounted && !this.pauseVideoActivities) {
      this.onImageCaptured({
        result: CapturedImageResult.FAILED_DECODE_TIMEOUT,
      });
    }
  };

  public captureImage = () => {
    if (!this.componentIsMounted) {
      return;
    }
    if (!this.cornerDetector.currentlyInFrame && !this.inFrameOverride) {
      setTimeout(this.captureImage, 33);
      return;
    }
    const scale = 1;
    const imageData = this.getImageData(scale);
    if (!imageData) {
      return;
    }
    const canvas = imageData.canvas;
    const capturedImage: CapturedImage = {
      image: canvas.toDataURL ("image/png"),
      localDecodeAttempts: this.decoderPairs.length > 0 ? this.state.attempts : undefined,
      resolution: {
        height: canvas.height,
        width: canvas.width,
      },
      result: CapturedImageResult.CAPTURED,
    };
    this.onImageCaptured(capturedImage);
  };

  private inFrame = () => {
    if (!this.componentIsMounted || this.pauseVideoActivities || this.inFrameOverride) {
      if (this.props.debug && !!this.state.overlayContext) {
        this.state.overlayContext.clearRect(0, 0, this.state.overlayContext.canvas.width,
                                                         this.state.overlayContext.canvas.height);
      }
      return;
    }
    const scale = 0.25;
    const overlayImage = this.getImageData(scale);

    if (!overlayImage) {
          return;
    }
    const {canvas, ctx, width, height} = overlayImage;

    if (!!ctx && !!this.state.overlayContext) {
       this.state.overlayContext.canvas.width = width * (1 / scale);
       this.state.overlayContext.canvas.height = height * (1 / scale);
       const frame = ctx.getImageData(0, 0, width, height);
       const result = CornerDetection.driverLicenseIsInFrame(
         frame.data,
         frame.width,
         frame.height,
         this.cornerDetector.threshold,
         this.state.failedInFrame,
         this.cornerDetector.inverseThreshold,
       );

       if (result[0]) {
         this.cornerDetector.consecutiveInFrame!! += 1;
       } else {
        this.cornerDetector.consecutiveInFrame = 0;
       }

       if (this.cornerDetector.consecutiveInFrame!! >= 10) {
         this.cornerDetector.currentlyInFrame = true;
       } else {
         this.cornerDetector.currentlyInFrame = false;
       }

       this.cornerDetector.threshold = result[2];
       this.cornerDetector.inverseThreshold = result[3];

       if (this.cornerDetector.currentlyInFrame) {
         if (this.props.debug) {
           this.drawOverlay(result[1], scale);
         }
       } else {
         if (this.props.debug) {
           this.state.overlayContext.clearRect(0, 0, width, height);
         }
         const failedInFrame = this.state.failedInFrame !== undefined ? this.state.failedInFrame + 1 : 1;
         this.setState({
           failedInFrame,
         });
       }
      }
    setTimeout(this.inFrame, 33);
  };

  private drawOverlay = (corners: number[][], scale: number) => {
    const ctx2 = this.state.overlayContext;

    if (!!ctx2) {
      ctx2.strokeStyle = "blue";
      ctx2.lineWidth = 5;
      ctx2.beginPath();
      for (let i = 0; i < corners.length; i++) {
        ctx2.moveTo(corners[i][0] * (1 / scale), corners[i][1] * (1 / scale));
        ctx2.lineTo(corners[(i + 1) % 4][0] * (1 / scale), corners[(i + 1) % 4][1] * (1 / scale));
      }
      ctx2.stroke();
    }
  };

  private getImageAndDecode = () => {
    if (!this.componentIsMounted || this.pauseVideoActivities) {
      return;
    }

    if (!this.cornerDetector.currentlyInFrame && !this.inFrameOverride) {
      setTimeout(this.getImageAndDecode, 33);
      return;
    }
    if (this.decoderPairs.length > 0 && !this.decoderPairs.find((pair) => pair.isReady())) {
      // wait for a worker to be ready
      setTimeout(this.getImageAndDecode, 10);
      return;
    }

    const imageData = this.getImageData(1);
    if (!imageData) {
      return;
    }
    const decoderPair = this.decoderPairs.find((it) => it.isReady());
    if (!!decoderPair) {
      setTimeout(this.getImageAndDecode, 33);
      decoderPair.startDecode(imageData.canvas, imageData.ctx, imageData.height, imageData.width);
    } else {
      // wait for a web worker to be ready
      setTimeout(this.getImageAndDecode, 10);
    }

    const attempts = this.state.attempts;
    if (attempts === 0) {
      PubSubEvents.localDecodeStarted();
    }
    this.setState({
      attempts: attempts + 1,
    });
    return;
  };

  public getImageData = (scale: number): OverlayCanvas | undefined => {
    const videoElement = this.state.videoElement;
    const track = this.getFirstTrack();
    if (!videoElement || !track) {
      return undefined;
    }

    const canvas = document.createElement("canvas");
    const videoWidth = videoElement.videoWidth;
    const videoHeight = videoElement.videoHeight;

    let resolution: Resolution;
    // decoding locally gets slow above 720
    if (this.decoderPairs.length > 0) {
      resolution = SevenTwenty;
    } else {
      resolution = this.props.resolution;
    }

    const resolutionWidth = resolution.width;
    const resolutionHeight = resolution.height;

    let canvasWidth;
    let canvasHeight;

    // don't blow image up
    if (videoWidth < resolutionWidth || videoHeight < resolutionHeight) {
      canvasWidth = videoWidth;
      canvasHeight = videoHeight;
    } else {
      // height width changes flip depending on phone rotation
      if (videoHeight < videoWidth) {
        canvasHeight = resolution.height;
        canvasWidth = resolution.width;
      } else {
        canvasHeight = resolution.width;
        canvasWidth = resolution.height;
      }
    }

    canvas.width = canvasWidth * scale;
    canvas.height = canvasHeight * scale;

    const ctx = canvas.getContext("2d");
    if (!!ctx) {
      ctx.drawImage(videoElement, 0, 0, videoWidth, videoHeight, 0, 0, canvas.width, canvas.height);
      return {
        canvas,
        ctx,
        height: canvas.height,
        width: canvas.width,
      };
    } else {
      return undefined;
    }
  };
}



// WEBPACK FOOTER //
// ./src/components/camera/Video.tsx